import Quill from 'quill';

const Module = Quill.import('core/module') as any;

const MEDIA_BLOT_NAMES = new Set(['image', 'video']);
const MIN_WIDTH = 48;
const HANDLES = ['nw', 'ne', 'sw', 'se'] as const;

type HandleCorner = (typeof HANDLES)[number];

export class EulaMediaResize extends Module {
  readonly editor: any;
  overlay: HTMLDivElement | null = null;
  selected: HTMLElement | null = null;
  hideTimer: number | null = null;
  drag: { corner: HandleCorner; startX: number; startWidth: number } | null = null;

  constructor(quill: any, options: unknown) {
    super(quill, options);
    this.editor = quill;

    this.editor.root.addEventListener('mousedown', this.onRootMouseDown);
    this.editor.root.addEventListener('click', this.onRootClick);
    this.editor.root.addEventListener('scroll', this.onScroll);
    this.editor.on('selection-change', this.onSelectionChange);
    this.editor.on('text-change', this.onScroll);
    document.addEventListener('keydown', this.onKeyDown, true);

    this.addDeleteBindings();
  }

  addDeleteBindings() {
    const keyboard = this.editor.keyboard;
    keyboard.addBinding({ key: 'Backspace', collapsed: true }, (range: { index: number }) => {
      if (this.deleteIfSelected()) return false;
      if (range.index === 0) return true;
      const [prev] = this.editor.getLeaf(range.index - 1);
      if (MEDIA_BLOT_NAMES.has(prev?.statics?.blotName)) {
        this.editor.deleteText(range.index - 1, 1, 'user');
        return false;
      }
      return true;
    });
    keyboard.addBinding({ key: 'Delete', collapsed: true }, (range: { index: number }) => {
      if (this.deleteIfSelected()) return false;
      const [next] = this.editor.getLeaf(range.index);
      if (MEDIA_BLOT_NAMES.has(next?.statics?.blotName)) {
        this.editor.deleteText(range.index, 1, 'user');
        return false;
      }
      return true;
    });

    keyboard.addBinding({ key: 'Enter' }, (range: { index: number; length: number }) => {
      if (this.insertLineAfterSelected()) return false;
      const [blot] = this.editor.getLeaf(range.index);
      if (MEDIA_BLOT_NAMES.has(blot?.statics?.blotName)) {
        this.editor.insertText(range.index + 1, '\n', 'user');
        this.editor.setSelection(range.index + 1, 0, 'user');
        return false;
      }
      return true;
    });

    this.preferLastBinding('Backspace');
    this.preferLastBinding('Delete');
    this.preferLastBinding('Enter');
  }

  preferLastBinding(key: string) {
    const list = this.editor.keyboard.bindings[key];
    if (Array.isArray(list) && list.length > 1) {
      list.unshift(list.pop());
    }
  }

  onRootMouseDown = (event: MouseEvent) => {
    const media = (event.target as HTMLElement | null)?.closest?.('img, video') as HTMLElement | null;
    if (media?.tagName === 'VIDEO' && this.editor.root.contains(media)) {
      event.preventDefault();
    }
  };

  onRootClick = (event: MouseEvent) => {
    const media = (event.target as HTMLElement | null)?.closest?.('img, video') as HTMLElement | null;
    if (!media || !this.editor.root.contains(media)) return;

    const blot = Quill.find(media) as any;
    if (!blot) return;
    const index = this.editor.getIndex(blot);
    this.editor.focus();
    this.editor.setSelection(index, 1, 'user');
    this.show(media);
  };

  onSelectionChange = (range: { index: number; length: number } | null) => {
    if (!this.editor.root.isConnected) {
      this.teardown();
      return;
    }
    if (range?.length === 1) {
      const [blot] = this.editor.getLeaf(range.index);
      if (blot && MEDIA_BLOT_NAMES.has(blot.statics?.blotName) && blot.domNode) {
        this.show(blot.domNode as HTMLElement);
        return;
      }
    }
    this.scheduleHide();
  };

  onKeyDown = (event: KeyboardEvent) => {
    if (!this.editor.root.isConnected) {
      this.teardown();
      return;
    }
    if (!this.selected) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    const active = document.activeElement as HTMLElement | null;
    if (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) && !this.editor.root.contains(active)) {
      return;
    }

    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      event.stopPropagation();
      this.deleteIfSelected();
      return;
    }

    if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      this.insertLineAfterSelected(event.key === 'Enter');
    }
  };

  insertLineAfterSelected(insertNewline = true): boolean {
    if (!this.selected) return false;
    const blot = Quill.find(this.selected) as any;
    if (!blot) {
      this.hide();
      return false;
    }
    const after = this.editor.getIndex(blot) + 1;
    this.hide();
    this.editor.focus();
    if (insertNewline) {
      this.editor.insertText(after, '\n', 'user');
    }
    this.editor.setSelection(after, 0, 'user');
    return true;
  }

  onScroll = () => {
    if (this.selected) {
      if (!this.selected.isConnected) {
        this.hide();
        return;
      }
      this.positionOverlay();
    }
  };

  show(media: HTMLElement) {
    this.clearHideTimer();
    this.selected = media;
    if (!this.overlay) {
      this.overlay = this.createOverlay();
      this.editor.container.appendChild(this.overlay);
    }
    this.overlay.style.display = 'block';
    this.positionOverlay();
  }

  scheduleHide() {
    this.clearHideTimer();
    this.hideTimer = window.setTimeout(() => this.hide(), 0);
  }

  clearHideTimer() {
    if (this.hideTimer != null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  hide() {
    this.clearHideTimer();
    this.selected = null;
    this.drag = null;
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
  }

  deleteIfSelected(): boolean {
    if (!this.selected) return false;
    const blot = Quill.find(this.selected) as any;
    if (!blot) {
      this.hide();
      return false;
    }
    const index = this.editor.getIndex(blot);
    this.hide();
    this.editor.deleteText(index, 1, 'user');
    this.editor.focus();
    this.editor.setSelection(index, 0, 'silent');
    return true;
  }

  createOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = 'eula-media-overlay';

    HANDLES.forEach((corner) => {
      const handle = document.createElement('span');
      handle.className = `eula-media-handle eula-media-handle-${corner}`;
      handle.addEventListener('mousedown', (event) => this.startDrag(event, corner));
      overlay.appendChild(handle);
    });

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'eula-media-delete';
    const deleteLabel = this.editor.container?.getAttribute('data-delete-label') || 'Delete';
    remove.title = deleteLabel;
    remove.setAttribute('aria-label', deleteLabel);
    remove.addEventListener('mousedown', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
    remove.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.deleteIfSelected();
    });
    overlay.appendChild(remove);

    return overlay;
  }

  startDrag(event: MouseEvent, corner: HandleCorner) {
    if (!this.selected) return;
    event.preventDefault();
    event.stopPropagation();
    this.drag = {
      corner,
      startX: event.clientX,
      startWidth: this.selected.getBoundingClientRect().width
    };
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.onDragEnd);
  }

  onDrag = (event: MouseEvent) => {
    if (!this.drag || !this.selected) return;
    const dx = event.clientX - this.drag.startX;
    const sign = this.drag.corner === 'nw' || this.drag.corner === 'sw' ? -1 : 1;
    const maxWidth = this.editor.root.clientWidth - 8;
    const width = Math.round(Math.min(maxWidth, Math.max(MIN_WIDTH, this.drag.startWidth + dx * sign)));
    this.selected.style.width = `${width}px`;
    this.selected.removeAttribute('height');
    this.positionOverlay();
  };

  onDragEnd = () => {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragEnd);
    if (!this.drag || !this.selected) {
      this.drag = null;
      return;
    }

    const width = Math.round(this.selected.getBoundingClientRect().width);
    this.selected.style.width = '';
    this.selected.removeAttribute('height');
    this.selected.setAttribute('width', String(width));

    const blot = Quill.find(this.selected) as any;
    if (blot) {
      const index = this.editor.getIndex(blot);
      this.editor.formatText(index, 1, 'width', String(width), 'user');
      this.editor.formatText(index, 1, 'height', false, 'user');
      this.editor.setSelection(index, 1, 'silent');
    }

    this.drag = null;
    this.positionOverlay();
  };

  positionOverlay() {
    if (!this.overlay || !this.selected) return;
    const mediaRect = this.selected.getBoundingClientRect();
    const containerRect = this.editor.container.getBoundingClientRect();
    this.overlay.style.left = `${mediaRect.left - containerRect.left}px`;
    this.overlay.style.top = `${mediaRect.top - containerRect.top}px`;
    this.overlay.style.width = `${mediaRect.width}px`;
    this.overlay.style.height = `${mediaRect.height}px`;
  }

  teardown() {
    this.hide();
    document.removeEventListener('keydown', this.onKeyDown, true);
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.onDragEnd);
  }
}
